import type { FeatureCollection, LineString } from "geojson"
import { ACTIONS, type PastRouteStop } from "@/reducers/routes2gSlice"
import {
  pastStopsFeatureCollection,
  type PastStopFeatureProps,
} from "@/lib/pastStops"
import { isSameWarsawDay, parseWarsawTime } from "@/lib/warsawTime"

/**
 * The part of the current delivery order the driver is already done with: the
 * stops they left or skipped, and the path they actually drove (`[lng, lat]`).
 */
export type PastStopsOverlay = {
  stops: PastRouteStop[]
  trail: [number, number][]
}

export const PAST_TRAIL_SOURCE_ID = "past-trail-source"
export const PAST_TRAIL_LAYER_ID = "past-trail-layer"
// White casing under the dashed trail, so it stays visible on satellite tiles.
export const PAST_TRAIL_CASING_LAYER_ID = "past-trail-casing-layer"
export const PAST_STOPS_SOURCE_ID = "past-stops-source"
export const PAST_STOPS_LAYER_ID = "past-stops-layer"

export const PAST_STOP_LEFT_IMAGE_ID = "past-stop-left"
export const PAST_STOP_SKIPPED_IMAGE_ID = "past-stop-skipped"

// White ring so the marker stays readable on light, dark and satellite tiles.
export const PAST_STOP_LEFT_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#16a34a" stroke="#ffffff" stroke-width="2"/><path d="m7.5 12.5 3 3 6-6.5" fill="none" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
export const PAST_STOP_SKIPPED_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#d97706" stroke="#ffffff" stroke-width="2"/><path d="M12 6.5v7" fill="none" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round"/><circle cx="12" cy="17" r="1.5" fill="#ffffff"/></svg>'

export const pastTrailColor = (isDarkMode: boolean) =>
  isDarkMode ? "#9ca3af" : "#6b7280"

/** Layers every route line is drawn with; the driven trail goes below them. */
const isRouteLineLayer = (layerId: string) =>
  layerId.startsWith("maplibre-gl-directions-") ||
  layerId === "reference-route-layer" ||
  layerId === "driver-route-layer"

/**
 * The layer the driven trail should be inserted before so that it lies under
 * every route line, or `undefined` when no route line exists yet (the trail
 * then goes on top, and the route lines added later end up above it).
 */
export function getBelowRouteLinesBeforeId(
  layersOrder: string[],
): string | undefined {
  return layersOrder.find(isRouteLineLayer)
}

const EMPTY_LINE: FeatureCollection<LineString> = {
  type: "FeatureCollection",
  features: [],
}

/** Fewer than two points is not a line, so nothing is drawn. */
export function pastTrailFeatureCollection(
  trail: [number, number][],
): FeatureCollection<LineString> {
  if (trail.length < 2) return EMPTY_LINE
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: {},
        geometry: { type: "LineString", coordinates: trail },
      },
    ],
  }
}

/** Every `[lng, lat]` the overlay draws, for fitting the map to it. */
export function pastStopsOverlayPoints(
  overlay: PastStopsOverlay | null,
): [number, number][] {
  if (!overlay) return []
  const stopPoints = pastStopsFeatureCollection(overlay.stops).features.map(
    (feature) => feature.geometry.coordinates as [number, number],
  )
  const trailPoints = overlay.trail.length >= 2 ? overlay.trail : []
  return [...trailPoints, ...stopPoints]
}

/**
 * When gps's local `arrived_at` ("2026-10-27T09:15:00", Warsaw time, no zone)
 * was: "HH:MM" on the same Warsaw day as `now`, "DD.MM HH:MM" otherwise, so a
 * multi-day order stays unambiguous. The digits are read straight from the
 * string: parsing it as a Date would shift it by the browser's offset.
 * Anything unrecognised gives "".
 */
export function formatArrivedTime(
  arrivedAt: string,
  now: number = Date.now(),
): string {
  const match = /^\d{4}-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(arrivedAt)
  const at = parseWarsawTime(arrivedAt)
  if (!match || at === null) return ""
  const [, month, day, hour, minute] = match
  const time = `${hour}:${minute}`
  return isSameWarsawDay(at, now) ? time : `${day}.${month} ${time}`
}

export type PastStopPopupContent = {
  title: string
  details: string[]
  status: string
}

export function pastStopPopupContent(
  props: PastStopFeatureProps,
  now: number = Date.now(),
): PastStopPopupContent {
  const actionLabel =
    ACTIONS.find((action) => action.value === props.action)?.label ??
    props.action
  const arrivedTime = formatArrivedTime(props.arrivedAt, now)
  const status =
    props.status === "skipped"
      ? "Skipped"
      : arrivedTime
        ? `Arrived ${arrivedTime}`
        : "Completed"

  return {
    title: props.address || actionLabel || "Past stop",
    details: [
      ...(props.address && actionLabel ? [actionLabel] : []),
      ...(props.ref ? [`Ref: ${props.ref}`] : []),
    ],
    status,
  }
}
